"use client";

import * as React from "react";
import { Drawer as DrawerPrimitive } from "vaul";

import { cn } from "../../lib/utils";
import { PortalScopeProvider } from "../../lib/portal-scope";

const Drawer = ({
	shouldScaleBackground = true,
	...props
}: React.ComponentProps<typeof DrawerPrimitive.Root>) => (
	<DrawerPrimitive.Root
		shouldScaleBackground={shouldScaleBackground}
		{...props}
	/>
);
Drawer.displayName = "Drawer";

const DrawerNestedRoot = (
	props: React.ComponentProps<typeof DrawerPrimitive.NestedRoot>,
) => <DrawerPrimitive.NestedRoot {...props} />;
DrawerNestedRoot.displayName = "DrawerNestedRoot";

const DrawerTrigger = DrawerPrimitive.Trigger;
const DrawerPortal = DrawerPrimitive.Portal;
const DrawerClose = DrawerPrimitive.Close;

const DrawerOverlay = React.forwardRef<
	React.ElementRef<typeof DrawerPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<DrawerPrimitive.Overlay
		ref={ref}
		className={cn("fixed inset-0 z-[5000] bg-black/80", className)}
		{...props}
	/>
));
DrawerOverlay.displayName = "DrawerOverlay";

const DrawerContent = React.forwardRef<
	React.ElementRef<typeof DrawerPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Content>
>(({ className, children, ...props }, ref) => {
	const [portalContainer, setPortalContainer] =
		React.useState<HTMLElement | null>(null);

	const setRefs = React.useCallback(
		(node: HTMLDivElement | null) => {
			setPortalContainer(node);
			if (typeof ref === "function") {
				ref(node);
			} else if (ref) {
				(ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
			}
		},
		[ref],
	);

	return (
		<DrawerPortal>
			<DrawerOverlay />
			<DrawerPrimitive.Content
				ref={setRefs}
				className={cn(
					"fixed inset-x-0 bottom-0 z-[5000] mt-24 flex h-auto max-h-[calc(100vh-6rem)] flex-col rounded-t-[10px] border border-neutral-200 bg-popover",
					className,
				)}
				{...props}
			>
				<div className="mx-auto mt-4 h-2 w-[100px] shrink-0 rounded-full bg-neutral-200" />
				<PortalScopeProvider container={portalContainer}>
					<div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
						{children}
					</div>
				</PortalScopeProvider>
			</DrawerPrimitive.Content>
		</DrawerPortal>
	);
});
DrawerContent.displayName = "DrawerContent";

const DrawerHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn("grid gap-1.5 p-4 text-center sm:text-left", className)}
		{...props}
	/>
);
DrawerHeader.displayName = "DrawerHeader";

const DrawerFooter = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn("mt-auto flex flex-col gap-2 p-4", className)}
		{...props}
	/>
);
DrawerFooter.displayName = "DrawerFooter";

const DrawerTitle = React.forwardRef<
	React.ElementRef<typeof DrawerPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Title>
>(({ className, ...props }, ref) => (
	<DrawerPrimitive.Title
		ref={ref}
		className={cn(
			"text-lg font-semibold leading-none tracking-tight",
			className,
		)}
		{...props}
	/>
));
DrawerTitle.displayName = "DrawerTitle";

const DrawerDescription = React.forwardRef<
	React.ElementRef<typeof DrawerPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof DrawerPrimitive.Description>
>(({ className, ...props }, ref) => (
	<DrawerPrimitive.Description
		ref={ref}
		className={cn("text-sm text-neutral-500", className)}
		{...props}
	/>
));
DrawerDescription.displayName = "DrawerDescription";

export {
	Drawer,
	DrawerNestedRoot,
	DrawerPortal,
	DrawerOverlay,
	DrawerTrigger,
	DrawerClose,
	DrawerContent,
	DrawerHeader,
	DrawerFooter,
	DrawerTitle,
	DrawerDescription,
};
